import React from 'react';
import { Paper, Box, Typography, Chip, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

/** Matches Sales order / work order summary strips */
export const procurementSummaryPaperSx = {
  p: 1.5,
  mb: 1.5,
  backgroundColor: 'surfaceMuted.main',
  border: 1,
  borderColor: 'borderSubtle.main'
};

export const procurementFieldCaptionSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  display: 'block'
};

/**
 * Keeps summary labels, values, links, and status chips on consistent rows.
 * Nested multi-column groups can opt out with className="wm-summary-compound".
 */
export const procurementSummaryFieldsRowSx = (dense = false) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-start',
  alignContent: 'flex-start',
  columnGap: dense ? 0.75 : 1,
  rowGap: dense ? 0.5 : 0.75,
  width: '100%',
  minWidth: 0,
  '& > .MuiBox-root': {
    display: 'grid',
    gridTemplateRows: dense ? '14px minmax(24px, auto)' : '17px minmax(25px, auto)',
    alignItems: 'center',
    minWidth: 0,
    minHeight: dense ? 38 : 42,
    px: dense ? 0.75 : 1,
  },
  '& > .MuiBox-root:first-of-type': { pl: 0 },
  '& > .MuiBox-root.wm-summary-compound': {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 3,
    px: dense ? 0.75 : 1,
    minHeight: 0,
    gridTemplateRows: 'unset',
  },
  '& > .MuiBox-root > .MuiTypography-caption': {
    fontSize: dense ? '0.65rem' : '0.7rem',
    lineHeight: 1.15,
    color: 'text.secondary',
    display: 'block',
  },
  '& > .MuiBox-root > .MuiTypography-body2': {
    display: 'flex',
    alignItems: 'center',
    minHeight: dense ? 24 : 25,
    fontSize: dense ? '0.8125rem' : undefined,
    lineHeight: 1.25,
    ...(dense ? { whiteSpace: 'nowrap' } : {}),
  },
  '& > .MuiBox-root > .MuiChip-root': {
    alignSelf: 'center',
  },
});

/** Dense notes block for the fixed-height second row under `ProcurementSummaryBar` (when using `secondaryRow`). */
export function ProcurementSummaryNotesDense({ label = 'Notes', text }) {
  const raw = text != null ? String(text) : '';
  const display = raw.trim() ? raw : '—';
  return (
    <>
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.65rem',
          lineHeight: 1.1,
          color: 'text.secondary',
          display: 'block',
          m: 0,
          mb: 0.125,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          fontSize: '0.8125rem',
          lineHeight: 1.2,
          width: '100%',
          maxWidth: '100%',
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          whiteSpace: 'pre-wrap',
          m: 0,
        }}
      >
        {display}
      </Typography>
    </>
  );
}

/** Caps very long notes; short text keeps natural height (no empty band). */
const summaryNotesRowSx = (dense) => ({
  mt: dense ? 0.375 : 0.5,
  pt: dense ? 0.375 : 0.5,
  pb: 0.125,
  borderTop: '1px solid',
  borderColor: 'divider',
  maxHeight: dense ? 46 : 54,
  overflowY: 'auto',
  overflowX: 'hidden',
  flexShrink: 0,
  minWidth: 0,
  width: '100%',
  boxSizing: 'border-box',
});

export function ProcurementSummaryBar({ children, dense, secondaryRow, topBanner }) {
  return (
    <Paper
      sx={{
        ...procurementSummaryPaperSx,
        ...(dense ? { p: 1, mb: 1 } : {}),
        ...(secondaryRow != null
          ? { display: 'flex', flexDirection: 'column', boxSizing: 'border-box', minWidth: 0, maxWidth: '100%' }
          : {}),
      }}
    >
      {topBanner != null ? (
        <Box sx={{ width: '100%', minWidth: 0, mb: secondaryRow != null ? 1 : 1.25 }}>{topBanner}</Box>
      ) : null}
      {secondaryRow != null ? (
        <>
          <Box
            sx={procurementSummaryFieldsRowSx(dense)}
          >
            {children}
          </Box>
          <Box sx={summaryNotesRowSx(dense)}>{secondaryRow}</Box>
        </>
      ) : (
        <Box
          sx={procurementSummaryFieldsRowSx(dense)}
        >
          {children}
        </Box>
      )}
    </Paper>
  );
}

export function ProcurementSummaryField({ label, children, wide, dense }) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        ...(wide ? { flexGrow: 1, minWidth: dense ? 160 : 200 } : {}),
      }}
    >
      <Typography
        variant="caption"
        sx={{
          ...procurementFieldCaptionSx,
          ...(dense ? { fontSize: '0.65rem', lineHeight: 1.2, mb: 0 } : {}),
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={dense ? { fontSize: '0.8125rem', lineHeight: 1.3, mt: 0.1 } : {}}
      >
        {children}
      </Typography>
    </Box>
  );
}

const PO_STATUS_ORDERED = 'ORDERED';
const PO_STATUS_PARTIAL = 'PARTIALLY RCVD';
const PO_STATUS_FULL = 'RECEIVED IN FULL';
const PO_STATUS_CANCELLED = 'CANCELLED';
const PO_STATUS_DRAFT = 'DRAFT';

export function getPoReceiptStatusChipProps(status) {
  const s = (status || '').toString().trim().toUpperCase();
  if (s === PO_STATUS_CANCELLED) return { color: 'error', label: 'CANCELLED' };
  if (s === PO_STATUS_DRAFT) return { color: 'default', label: 'DRAFT' };
  if (s === PO_STATUS_PARTIAL) return { color: 'warning', label: 'PARTIALLY RCVD' };
  if (s === PO_STATUS_FULL) return { color: 'success', label: 'RECEIVED IN FULL' };
  return { color: 'info', label: PO_STATUS_ORDERED };
}

/** Friendlier labels for PO status on the work order "Linked POs" summary (Ordered / Received / …). */
export function getLinkedPoStatusPresentation(status) {
  const s = (status || '').toString().trim().toUpperCase();
  if (s === PO_STATUS_CANCELLED) return { color: 'error', label: 'Cancelled' };
  if (s === PO_STATUS_DRAFT) return { color: 'default', label: 'Draft' };
  if (s === PO_STATUS_PARTIAL) return { color: 'warning', label: 'Partially received' };
  if (s === PO_STATUS_FULL) return { color: 'success', label: 'Received' };
  if (s === PO_STATUS_ORDERED) return { color: 'info', label: 'Ordered' };
  return { color: 'info', label: 'Ordered' };
}

export function PoReceiptStatusChip({ status, size = 'small' }) {
  const { color, label } = getPoReceiptStatusChipProps(status);
  return <Chip label={label} color={color} size={size} sx={{ fontWeight: 500 }} />;
}

/** Shipment received: customer/vendor P.O. on paperwork; UNKNOWN PO when not tied to a PO and blank */
export function formatShipmentPoDisplay(row) {
  if (!row) return '—';
  if (row.purchase_order_id) return row.po_number || '—';
  const p = row.po_number != null ? String(row.po_number).trim() : '';
  return p || 'UNKNOWN PO';
}

/** Fabworks purchase order linked to this shipment (system identifier / number). */
export function formatFabworksPoDisplay(row) {
  if (!row) return '—';
  if (!row.purchase_order_id) return '—';
  const id = row.purchase_order_identifier != null ? String(row.purchase_order_identifier).trim() : '';
  if (id) return id;
  const num = row.po_number != null ? String(row.po_number).trim() : '';
  if (num) return num;
  return `PO #${row.purchase_order_id}`;
}

/** Linked shipment-in records for a PO summary strip (comma-separated links). */
export function ShipmentsReceivedSummaryLinks({ receivers }) {
  if (!receivers?.length) return '—';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, rowGap: 0.25 }}>
      {receivers.map((r, i) => (
        <Box key={r.id} component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {i > 0 ? (
            <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              ,
            </Typography>
          ) : null}
          <Link component={RouterLink} to={`/procurement/shipments-in/${r.id}`} variant="body2" underline="hover">
            {r.identifier ?? r.receiver_no ?? `#${r.id}`}
          </Link>
        </Box>
      ))}
    </Box>
  );
}
